import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Save, X } from "lucide-react";

interface MessageTemplate {
  id: string;
  name: string;
  template_key: string;
  category: string;
  subject_template: string;
  html_template: string; 
  text_template: string;
  sms_template?: string;
  variables: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface InlineTemplateEditorProps {
  template: MessageTemplate | null;
  onSave: (template: Partial<MessageTemplate>) => Promise<void>;
  onCancel: () => void;
}

export function InlineTemplateEditor({ template, onSave, onCancel }: InlineTemplateEditorProps) {
  const [formData, setFormData] = useState({
    name: '',
    template_key: '',
    category: 'notification',
    subject_template: '',
    html_template: '',
    text_template: '',
    sms_template: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        template_key: template.template_key,
        category: template.category,
        subject_template: template.subject_template,
        html_template: template.html_template,
        text_template: template.text_template,
        sms_template: template.sms_template || '',
        is_active: template.is_active
      });
    } else {
      setFormData({
        name: '',
        template_key: '',
        category: 'notification',
        subject_template: '',
        html_template: '',
        text_template: '',
        sms_template: '',
        is_active: true
      });
    }
  }, [template]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Template Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
            placeholder="Enter template name"
          />
        </div>
        <div>
          <Label htmlFor="template_key">Template Key</Label>
          <Input
            id="template_key"
            value={formData.template_key}
            onChange={(e) => setFormData(prev => ({...prev, template_key: e.target.value}))}
            placeholder="unique-template-key"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="category">Category</Label>
        <Input
          id="category"
          value={formData.category}
          onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
          placeholder="notification"
        />
      </div>

      <div>
        <Label htmlFor="subject">Subject Template</Label>
        <Input
          id="subject"
          value={formData.subject_template}
          onChange={(e) => setFormData(prev => ({...prev, subject_template: e.target.value}))}
          placeholder="Email subject with {{variables}}"
        />
      </div>

      <div>
        <Label htmlFor="html">HTML Template</Label>
        <Textarea
          id="html"
          value={formData.html_template}
          onChange={(e) => setFormData(prev => ({...prev, html_template: e.target.value}))}
          placeholder="HTML email content with {{variables}}"
          className="min-h-[200px]"
        />
      </div>

      <div>
        <Label htmlFor="text">Text Template</Label>
        <Textarea
          id="text"
          value={formData.text_template}
          onChange={(e) => setFormData(prev => ({...prev, text_template: e.target.value}))}
          placeholder="Plain text email content with {{variables}}"
          className="min-h-[100px]"
        />
      </div>

      <div>
        <Label htmlFor="sms">SMS Template (Optional)</Label>
        <Textarea
          id="sms"
          value={formData.sms_template}
          onChange={(e) => setFormData(prev => ({...prev, sms_template: e.target.value}))}
          placeholder="SMS message with {{variables}} - keep under 160 characters"
          className="min-h-[80px]"
        />
        <div className="text-sm text-muted-foreground mt-1">
          {formData.sms_template.length}/160 characters
          {formData.sms_template.length > 160 && (
            <span className="text-destructive ml-2">⚠ Message too long</span>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({...prev, is_active: checked}))}
        />
        <Label htmlFor="active">Template Active</Label>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>
    </div>
  );
}